import { useState } from "react";
import { Lock, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Blocks the app for Google-only accounts until they set a password, so the
// same account can later sign in with either Google or email+password.
export const SetPasswordGate = () => {
  const { user, signOut, setInitialPassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [show, setShow]         = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError("");
    setLoading(true);
    try {
      await setInitialPassword(password);
      toast.success("Password set! Welcome to Annapurna Home Pickles.");
    } catch (err) {
      setError(err?.message || "Could not set password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-[#F5E6CC]">
      <div
        data-testid="set-password-gate"
        className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-8 sm:p-10 max-w-md w-full shadow-sm"
      >
        <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-[#556B2F]/15 grid place-items-center">
          <KeyRound className="w-7 h-7 text-[#556B2F]" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#5A2D1D] text-center">
          One last step
        </h1>
        <p className="text-sm text-[#2E1A12]/75 text-center mt-2">
          You signed in with Google as <b>{user?.email}</b>. Set a password so you can also sign in with your email going forward.
        </p>

        <form onSubmit={submit} className="space-y-3 mt-6">
          <Field
            placeholder="New password (min. 6 characters)"
            value={password}
            onChange={setPassword}
            testid="set-password-input"
            show={show}
            toggle={
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50 hover:text-[#5A2D1D]"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />
          <Field
            placeholder="Confirm password"
            value={confirm}
            onChange={setConfirm}
            testid="set-password-confirm-input"
            show={show}
          />

          {error && (
            <div className="flex items-start gap-2 bg-[#EFD9B4] border border-[#5A2D1D]/40 text-[#5A2D1D] rounded-sm px-3 py-2 text-xs font-semibold">
              <span className="mt-0.5 flex-shrink-0">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <button
            data-testid="set-password-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-3 rounded-full font-semibold transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Set Password & Continue
          </button>
        </form>

        <button
          onClick={signOut}
          className="mt-4 w-full text-center text-xs font-semibold text-[#2E1A12]/50 hover:text-[#5A2D1D] transition-colors"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
};

const Field = ({ testid, value, onChange, placeholder, show, toggle }) => (
  <div className="relative">
    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A2D1D]/60" />
    <input
      data-testid={testid}
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required
      className={`w-full pl-10 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12] placeholder:text-[#2E1A12]/50 ${toggle ? "pr-10" : "pr-4"}`}
    />
    {toggle}
  </div>
);
